function getCartDrawerSectionId() {
  return document.querySelector("custom-cart-drawer")?.dataset.sectionId;
}

function getHeaderSectionId() {
  return document.querySelector("[data-header-section-id]")?.dataset
    .headerSectionId;
}

function getCartSections() {
  return [getCartDrawerSectionId(), getHeaderSectionId()]
    .filter(Boolean)
    .filter((id, index, arr) => arr.indexOf(id) === index)
    .join(",");
}

function getCartSectionsUrl() {
  return window.location.pathname;
}

class CustomCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.openTrigger = document.querySelector(`#${this.dataset.openTrigger}`);
    this.overlay = this.querySelector("#CustomCartOverlay");
    this.closeButton = this.querySelectorAll("[data-close]");
    this.cartCount = document.querySelector(".custom-cart-drawer__header h2");

    this.lastFreeShippingPercent = null;

    this.handleOpen = this.handleOpen.bind(this);
    this.cartRerender = this.cartRerender.bind(this);
    this.handleVanDetailsClick = this.handleVanDetailsClick.bind(this);
  }

  connectedCallback() {
    this.openTrigger?.addEventListener("click", this.handleOpen);
    this.closeButton.forEach((closeButton) => {
      closeButton.addEventListener("click", this.closeDrawer.bind(this));
    });
    document.addEventListener("click", (event) => {
      if (event.target === this.overlay) this.closeDrawer();
    });

    document.addEventListener("cart:rerender", this.cartRerender);
    this.addEventListener("click", this.handleVanDetailsClick);

    this.renderFreeShippingText();
  }

  disconnectedCallback() {
    this.openTrigger?.removeEventListener("click", this.handleOpen);
    document.removeEventListener("cart:rerender", this.cartRerender);
    this.removeEventListener("click", this.handleVanDetailsClick);
  }

  handleVanDetailsClick(event) {
    const openButton = event.target.closest("[data-van-details-open]");
    const closeButton = event.target.closest("[data-van-details-close]");

    if (openButton) {
      event.preventDefault();

      const item = openButton.closest(".custom-cart-drawer__item");
      const modal = item?.querySelector("[data-van-details-modal]");

      if (!modal) return;

      modal.hidden = false;
      modal.classList.add("is-open");

      document.body.classList.add("cart-van-details-open");
      return;
    }

    if (closeButton) {
      event.preventDefault();

      const modal = closeButton.closest("[data-van-details-modal]");

      if (!modal) return;

      modal.classList.remove("is-open");
      modal.hidden = true;

      document.body.classList.remove("cart-van-details-open");
    }
  }

  /* -----------------------------
    Currency + Money helpers
  ------------------------------ */

  getCurrencyRate() {
    const rate = Number(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  formatMoney(cents) {
    const amount = cents / 100;

    const locale = document.documentElement.lang || "en-US";
    const currency = Shopify.currency.active || "USD";


    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
    }).format(amount);
  }

  computeFreeShipping() {
    const el = this.querySelector("[data-free-shipping]");
    if (!el) {
      return {
        percent: 0,
        thresholdActiveCents: 0,
        totalCents: 0,
        remainingCents: 0,
        unlocked: false,
      };
    }

    const thresholdShopCents = Number(el.dataset.thresholdShopCents || 0);
    const totalCents = Number(el.dataset.totalCents || 0);
    const rate = this.getCurrencyRate();

    const thresholdActiveCents = Math.round(thresholdShopCents * rate);
    const remainingCents = Math.max(0, thresholdActiveCents - totalCents);

    const percent = thresholdActiveCents
      ? Math.min(100, Math.round((totalCents / thresholdActiveCents) * 100))
      : 0;

    return {
      percent,
      thresholdActiveCents,
      totalCents,
      remainingCents,
      unlocked: thresholdActiveCents > 0 && totalCents >= thresholdActiveCents,
    };
  }

  renderFreeShippingText() {
    const el = this.querySelector("[data-free-shipping]");
    if (!el) return;

    const text = el.querySelector(".cart-free-shipping__text");
    const bar = el.querySelector(".cart-free-shipping__bar");
    if (!text || !bar) return;

    const { unlocked, remainingCents, thresholdActiveCents, totalCents } =
      this.computeFreeShipping();

    text.innerHTML = unlocked
      ? `You’ve unlocked FREE SHIPPING!`
      : `You’re ${this.formatMoney(remainingCents)} away from free shipping.`;

    bar.setAttribute("aria-valuemax", String(thresholdActiveCents));
    bar.setAttribute("aria-valuenow", String(totalCents));
  }

  getCurrentFreeShippingPercent() {
    const el = this.querySelector("[data-free-shipping]");
    if (!el) return null;

    const fill = el.querySelector(".cart-free-shipping__fill");
    if (!fill || !fill.parentElement) return this.lastFreeShippingPercent;

    const fillW = parseFloat(getComputedStyle(fill).width) || 0;
    const parentW = parseFloat(getComputedStyle(fill.parentElement).width) || 0;
    if (!parentW) return this.lastFreeShippingPercent;

    const p = Math.round((fillW / parentW) * 100);
    return Number.isFinite(p) ? p : this.lastFreeShippingPercent;
  }

  animateFreeShippingProgress(fromPercent = null) {
    const el = this.querySelector("[data-free-shipping]");
    if (!el) return;

    this.renderFreeShippingText();

    const { percent: toPercent } = this.computeFreeShipping();
    const fill = el.querySelector(".cart-free-shipping__fill");
    if (!fill) return;

    if (fromPercent === null || !Number.isFinite(fromPercent)) {
      fromPercent =
        this.lastFreeShippingPercent !== null
          ? this.lastFreeShippingPercent
          : 0;
    }

    fill.style.transition = "none";
    fill.style.width = `${fromPercent}%`;

    fill.getBoundingClientRect();

    fill.style.transition = "width 0.55s ease";

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fill.style.width = `${toPercent}%`;
      });
    });

    this.lastFreeShippingPercent = toPercent;
  }

  /* -----------------------------
    Drawer handlers
  ------------------------------ */

  handleOpen(event) {
    event.preventDefault();
    this.openDrawer();
    this.animateFreeShippingProgress(this.lastFreeShippingPercent);
  }

  openDrawer() {
    this.setAttribute("open", "");
    const body = document.body;
    body.classList.add("cart-drawer-open");
  }

  closeDrawer() {
    this.removeAttribute("open");
    const body = document.body;
    body.classList.remove("cart-drawer-open");
  }

  cartRerender(event) {
    const prevPercent = this.getCurrentFreeShippingPercent();

    const drawerSectionId = this.dataset.sectionId;
    const headerSectionId = getHeaderSectionId();

    const newHTML = event.detail.sections?.[drawerSectionId];
    const newCount = headerSectionId
      ? event.detail.sections?.[headerSectionId]
      : null;

    if (!newHTML) {
      console.warn("Cart drawer section was not returned:", {
        drawerSectionId,
        sections: event.detail.sections,
      });
      return;
    }

    const fakeElement = document.createElement("div");
    fakeElement.innerHTML = newHTML;

    const newInner = fakeElement.querySelector(".custom-cart-drawer__inner");
    const currentInner = this.querySelector(".custom-cart-drawer__inner");

    if (newInner && currentInner) {
      currentInner.innerHTML = newInner.innerHTML;
    }

    if (newCount) {
      const fakeCount = document.createElement("div");
      fakeCount.innerHTML = newCount;

      const newHeaderCount = fakeCount.querySelector("[data-el-cart-count]");
      const currentHeaderCount = document.querySelector("[data-el-cart-count]");

      if (currentHeaderCount) {
        const count = Number(newHeaderCount?.textContent?.trim() || 0);
        const isEmpty = count <= 0;

        currentHeaderCount.textContent = isEmpty ? "" : String(count);

        currentHeaderCount.classList.toggle("is-hidden", isEmpty);
        currentHeaderCount.hidden = isEmpty;
        currentHeaderCount.setAttribute(
          "aria-hidden",
          isEmpty ? "true" : "false",
        );

        if (isEmpty) {
          currentHeaderCount.style.display = "none";
        } else {
          currentHeaderCount.style.display = "";
        }
      }
    }

    this.cartCount = this.querySelector(".custom-cart-drawer__header h2");

    this.animateFreeShippingProgress(prevPercent);
    this.openDrawer();
  }
}

customElements.define("custom-cart-drawer", CustomCartDrawer);

class AtcButton extends HTMLElement {
  constructor() {
    super();

    this.isSubmitting = false;
    this.requiresKachingSelection = this.hasAttribute(
      "data-require-kaching-selection",
    );

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.updateBuyButtonState = this.updateBuyButtonState.bind(this);

    this.refreshRefs();
  }

  refreshRefs() {
    this.submitButton =
      this.querySelector('button[type="submit"]') ||
      this.querySelector("add-to-cart-component button[type='submit']");

    this.submitForm =
      this.querySelector('form[action*="/cart/add"]') ||
      this.closest('form[action*="/cart/add"]') ||
      this.submitButton?.closest('form[action*="/cart/add"]');

    this.textButton =
      this.querySelector("[data-button-text]") ||
      this.submitButton?.querySelector("[data-button-text]") ||
      this.submitButton?.querySelector(
        ".add-to-cart-text__content span span",
      ) ||
      this.submitButton?.querySelector(".add-to-cart-text__content");

    if (this.textButton && !this.textButton.dataset.defaultText) {
      this.textButton.dataset.defaultText = this.textButton.textContent.trim();
    }
  }

  connectedCallback() {
    this.refreshRefs();

    this.submitForm?.addEventListener("submit", this.handleSubmit);
    this.submitButton?.addEventListener("click", this.handleClick, true);

    if (this.requiresKachingSelection) {
      this.observeKachingSelection();
      this.updateBuyButtonState();
    }
  }

  disconnectedCallback() {
    this.submitForm?.removeEventListener("submit", this.handleSubmit);
    this.submitButton?.removeEventListener("click", this.handleClick, true);
    this.kachingObserver?.disconnect();
    document.removeEventListener("click", this.updateBuyButtonState);
    clearTimeout(this.buttonStateTimer);
  }

  setButtonText(text) {
    if (!this.textButton) return;
    this.textButton.textContent = text;
  }

  resetButtonText() {
    if (!this.textButton) return;
    this.textButton.textContent =
      this.textButton.dataset.defaultText || "ADD TO CART";
  }

  setLoading(isLoading) {
    const card = this.closest(".el-cart-recommend-card");

    if (card) card.classList.toggle("is-loading", isLoading);
    if (this.submitButton) {
      this.submitButton.classList.toggle("is-loading", isLoading);
      this.submitButton.disabled = isLoading || this.submitButton.disabled;
    }

    if (isLoading) {
      this.setButtonText("Adding...");
    }

    this.isSubmitting = isLoading;
    this.updateBuyButtonState();
  }

  setSuccessState() {
    if (this.submitButton) {
      this.submitButton.classList.remove("is-loading");
      this.submitButton.classList.add("is-added");
      this.submitButton.disabled = false;
    }

    this.isSubmitting = false;
    this.setButtonText("Added");
    this.updateBuyButtonState();

    clearTimeout(this.buttonStateTimer);
    this.buttonStateTimer = setTimeout(() => {
      this.submitButton?.classList.remove("is-added");
      this.resetButtonText();
      this.updateBuyButtonState();
    }, 1500);
  }

  setErrorState() {
    if (this.submitButton) {
      this.submitButton.classList.remove("is-loading");
      this.submitButton.classList.remove("is-added");
      this.submitButton.disabled = false;
    }

    this.isSubmitting = false;
    this.setButtonText("Failed to Add");
    this.updateBuyButtonState();

    clearTimeout(this.buttonStateTimer);
    this.buttonStateTimer = setTimeout(() => {
      this.resetButtonText();
      this.updateBuyButtonState();
    }, 1500);
  }

  hasKachingSelection() {
    return !!document.querySelector(".kaching-bundles__bar--selected");
  }

  updateBuyButtonState() {
    if (!this.submitButton) return;

    if (this.requiresKachingSelection) {
      const hasSelectedBundle = this.hasKachingSelection();
      this.submitButton.disabled = this.isSubmitting || !hasSelectedBundle;
      return;
    }

    this.submitButton.disabled = this.isSubmitting;
  }

  observeKachingSelection() {
    this.kachingObserver = new MutationObserver(() => {
      this.updateBuyButtonState();
    });

    this.kachingObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    document.addEventListener("click", this.updateBuyButtonState);
  }

  buildPayload() {
    if (!this.submitForm) {
      throw new Error("Cart form not found.");
    }

    const formData = new FormData(this.submitForm);

    const item = {
      id: formData.get("id"),
      quantity: Number(formData.get("quantity") || 1),
    };

    if (!item.id) {
      throw new Error("Variant ID not found.");
    }

    const sellingPlan = formData.get("selling_plan");
    if (sellingPlan) item.selling_plan = sellingPlan;

    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.*)\]$/);
      if (match) properties[match[1]] = value;
    }

    if (Object.keys(properties).length) {
      item.properties = properties;
    }

    return {
      items: [item],
      sections: getCartSections(),
      sections_url: getCartSectionsUrl(),
    };
  }

  async submitViaAjax() {
    if (this.isSubmitting) return;

    this.refreshRefs();

    if (!this.submitForm || !this.submitButton) return;

    if (this.requiresKachingSelection && !this.hasKachingSelection()) {
      this.updateBuyButtonState();
      return;
    }

    this.setLoading(true);

    try {
      const payload = this.buildPayload();

      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) throw data;

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:rerender", {
          detail: data,
          bubbles: true,
        }),
      );

      document.dispatchEvent(
        new CustomEvent("cartQuantityUpdated", {
          detail: data,
          bubbles: true,
        }),
      );

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: data,
          bubbles: true,
        }),
      );

      this.setSuccessState();
    } catch (error) {
      console.error("Add to cart failed:", error);
      this.setErrorState();
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    await this.submitViaAjax();
  }

  async handleClick(event) {
    if (!this.submitButton) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }

    await this.submitViaAjax();
  }
}

if (!customElements.get("atc-button")) {
  customElements.define("atc-button", AtcButton);
}

class VariantSelectorSync {
  constructor(container = document) {
    this.container = container;
    this.selectors = this.container.querySelectorAll("[data-variant-selector]");
    this.handleChange = this.handleChange.bind(this);
    this.init();
  }

  init() {
    this.selectors.forEach((select) => {
      select.addEventListener("change", this.handleChange);
      this.syncVariant(select);
    });
  }

  destroy() {
    this.selectors.forEach((select) => {
      select.removeEventListener("change", this.handleChange);
    });
  }

  handleChange(event) {
    const select = event.currentTarget;
    this.syncVariant(select);
  }

  syncVariant(select) {
    const selectedVariantId = select.value;
    const atcButton = select.parentElement?.nextElementSibling;

    if (!atcButton || atcButton.tagName.toLowerCase() !== "atc-button") return;

    const variantInput = atcButton.querySelector('[name="id"]');
    if (!variantInput) return;

    variantInput.value = selectedVariantId;
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new VariantSelectorSync();
});

class CartCheckbox extends HTMLElement {
  constructor() {
    super();
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.variantId = this.dataset.id;
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.handleChange = this.handleChange.bind(this);
    this.checkbox?.addEventListener("change", this.handleChange);
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener("change", this.handleChange);
  }

  setLoading(isLoading) {
    this.isSubmitting = isLoading;
    if (this.checkbox) this.checkbox.disabled = isLoading;
    this.classList.toggle("is-loading", isLoading);
  }

  async handleChange(event) {
    if (this.isSubmitting) return;

    const isChecked = event.target.checked;
    this.setLoading(true);

    try {
      const data = isChecked ? await this.addItem() : await this.removeItem();

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:rerender", {
          detail: data,
          bubbles: true,
        }),
      );
    } catch (error) {
      console.error("Cart checkbox error:", error);
      this.checkbox.checked = !isChecked;
    } finally {
      this.setLoading(false);
    }
  }

  async addItem() {
    const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [{ id: this.variantId, quantity: 1 }],
        sections: getCartSections(),
        sections_url: getCartSectionsUrl(),
      }),
    });

    if (!response.ok) throw new Error("Failed to add item");
    return response.json();
  }

  async removeItem() {
    const cart = await fetch(window.Shopify.routes.root + "cart.js").then((r) =>
      r.json(),
    );

    const matchedItem = cart.items.find(
      (item) => String(item.variant_id) === String(this.variantId),
    );

    const response = await fetch(
      window.Shopify.routes.root + "cart/change.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: matchedItem.key,
          quantity: 0,
          sections: getCartSections(),
          sections_url: getCartSectionsUrl(),
        }),
      },
    );

    if (!response.ok) throw new Error("Failed to remove item");
    return response.json();
  }
}

customElements.define("cart-checkbox", CartCheckbox);

const CART_SECTIONS = "el-custom-cart-drawer,el-header";

async function cartRequest(endpoint, payload) {
  const response = await fetch(window.Shopify.routes.root + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      data?.description || data?.message || `Request failed: ${endpoint}`;
    throw new Error(message);
  }

  return data;
}

function dispatchCartRerender(data) {
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:rerender", {
      detail: data,
      bubbles: true,
    }),
  );
}

class CartActions extends HTMLElement {
  constructor() {
    super();

    this.plusButton = this.querySelector("[data-plus]");
    this.minusButton = this.querySelector("[data-minus]");
    this.handleChange = this.handleChange.bind(this);

    this.isUpdating = false;
  }

  connectedCallback() {
    this.plusButton?.addEventListener("click", this.handleChange);
    this.minusButton?.addEventListener("click", this.handleChange);
  }

  disconnectedCallback() {
    this.plusButton?.removeEventListener("click", this.handleChange);
    this.minusButton?.removeEventListener("click", this.handleChange);
  }

  setLoading(isLoading) {
    const item = this.closest(".custom-cart-drawer__item");
    if (!item) return;

    item.classList.toggle("is-loading", isLoading);

    if (this.plusButton) this.plusButton.disabled = isLoading;
    if (this.minusButton) this.minusButton.disabled = isLoading;

    const variantSelect = item.querySelector("[data-variant-select]");
    if (variantSelect) variantSelect.disabled = isLoading;

    this.isUpdating = isLoading;
  }

  handleChange(event) {
    event.preventDefault();
    if (this.isUpdating) return;

    const nextQty = Number(event.currentTarget.dataset.quantity);

    if (Number.isNaN(nextQty) || nextQty < 0) return;

    this.setLoading(true);

    const formData = {
      line: this.dataset.line,
      quantity: nextQty,
      sections: getCartSections(),
      sections_url: getCartSectionsUrl(),
    };

    cartRequest("cart/change.js", formData)
      .then((data) => {
        dispatchCartRerender(data);
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setLoading(false);
      });
  }
}

class CartItemVariantSelect extends HTMLElement {
  constructor() {
    super();

    this.select = this.querySelector("[data-variant-select]");
    this.handleVariantChange = this.handleVariantChange.bind(this);

    this.isUpdating = false;
  }

  connectedCallback() {
    this.select?.addEventListener("change", this.handleVariantChange);
  }

  disconnectedCallback() {
    this.select?.removeEventListener("change", this.handleVariantChange);
  }

  get currentVariantId() {
    return String(this.dataset.currentVariantId || "");
  }

  get quantity() {
    return Number(this.dataset.quantity || 1);
  }

  get line() {
    return Number(this.dataset.line || 0);
  }

  get lineKey() {
    return this.dataset.key || "";
  }

  get properties() {
    const propertiesEl = this.querySelector("[data-line-properties]");
    if (!propertiesEl) return {};

    try {
      const parsed = JSON.parse(propertiesEl.textContent.trim() || "{}");
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.warn("Could not parse line item properties", error);
      return {};
    }
  }

  get currentSellingPlan() {
    return (
      this.querySelector("[data-current-selling-plan]")?.value?.trim() || ""
    );
  }

  setLoading(isLoading) {
    const item = this.closest(".custom-cart-drawer__item");
    if (!item) return;

    item.classList.toggle("is-loading", isLoading);

    if (this.select) this.select.disabled = isLoading;

    item.querySelectorAll("[data-plus], [data-minus]").forEach((button) => {
      button.disabled = isLoading;
    });

    this.isUpdating = isLoading;
  }

  async handleVariantChange(event) {
    if (this.isUpdating) return;

    const newVariantId = String(event.target.value || "");
    const oldVariantId = this.currentVariantId;

    if (!newVariantId || newVariantId === oldVariantId) return;

    this.setLoading(true);

    try {
      const selectedOption = event.target.selectedOptions?.[0];
      const supportedSellingPlanIds = (
        selectedOption?.dataset.sellingPlanIds || ""
      )
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);

      const addPayload = {
        items: [
          {
            id: Number(newVariantId),
            quantity: this.quantity,
          },
        ],
      };

      const properties = this.properties;
      if (Object.keys(properties).length > 0) {
        addPayload.items[0].properties = properties;
      }

      if (
        this.currentSellingPlan &&
        supportedSellingPlanIds.includes(this.currentSellingPlan)
      ) {
        addPayload.items[0].selling_plan = Number(this.currentSellingPlan);
      }

      await cartRequest("cart/add.js", addPayload);

      const removePayload = {
        quantity: 0,
        sections: getCartSections(),
        sections_url: getCartSectionsUrl(),
      };

      if (this.lineKey) {
        removePayload.id = String(this.lineKey);
      } else {
        removePayload.line = this.line;
      }

      const data = await cartRequest("cart/change.js", removePayload);

      dispatchCartRerender(data);
    } catch (error) {
      console.error("Error changing variant:", error);

      if (this.select) {
        this.select.value = oldVariantId;
      }

      this.setLoading(false);
    }
  }
}

if (!customElements.get("cart-actions")) {
  customElements.define("cart-actions", CartActions);
}

if (!customElements.get("cart-item-variant-select")) {
  customElements.define("cart-item-variant-select", CartItemVariantSelect);
}
